import type { Schemas } from '@argzombie/api-client';
import { ZOMBIE_STEALTH_DEFAULTS as ZS, type BaseModuleDef } from '../../lib/validate';
import { MAX_BEDS_PER_LEVEL } from '../../lib/validate/homebase';
import {
  CheckboxField,
  Fieldset,
  ItemIdListEditor,
  ListEditor,
  NumberField,
  SelectField,
  StackListEditor,
  StringListEditor,
  TextField,
} from './fields';
import { ROOM_SLOTS } from '../../lib/s2/types';
import { RequirementsEditor } from './s2/TechForms';
import { useContentRefs } from './refs';
import type { FormProps } from './types';
import {
  INFIRMARY_REST_MULTS,
  ZOMBIE_ATTACKS,
  ZOMBIE_WIND_DEFAULTS,
} from '../../lib/wind/defaults';

/** ADR 0066: `challenge` (1..10, default 5) scales the skill points a kill teaches. */
type Zombie = Schemas['ZombieDef'] & { challenge?: number };
type Module = BaseModuleDef;
type Category = Schemas['BuildingCategoryDef'];

/** Id, name (and description) shared by most entries. */
function IdentityFields<T extends { id?: string; name?: string; description?: string }>({
  value,
  onChange,
  errors,
  description,
}: FormProps<T> & { description?: boolean }) {
  return (
    <>
      <TextField
        label="Id"
        mono
        value={value.id}
        issues={errors.id}
        onChange={(v) => onChange({ ...value, id: v })}
      />
      <TextField
        label="Name"
        value={value.name}
        issues={errors.name}
        onChange={(v) => onChange({ ...value, name: v })}
      />
      {description && (
        <TextField
          label="Description"
          multiline
          value={value.description}
          issues={errors.description}
          onChange={(v) => onChange({ ...value, description: v })}
        />
      )}
    </>
  );
}

export function ZombieForm({ value: z, onChange, errors }: FormProps<Zombie>) {
  const num = (k: keyof Zombie, label: string, opts: { integer?: boolean; hint?: string } = {}) => (
    <NumberField
      label={label}
      hint={opts.hint}
      integer={opts.integer}
      min={0}
      value={z[k] as number | undefined}
      issues={errors[k]}
      onChange={(v) => onChange({ ...z, [k]: v ?? 0 })}
    />
  );
  const stealthNum = (
    k: Exclude<keyof typeof ZS, 'stealthImmune'>,
    label: string,
    opts: { hint?: string; max?: number } = {},
  ) => (
    <NumberField
      label={label}
      hint={opts.hint}
      min={0}
      max={opts.max}
      step={0.05}
      value={z[k] ?? ZS[k]}
      issues={errors[k]}
      onChange={(v) => onChange({ ...z, [k]: v ?? 0 })}
    />
  );
  return (
    <>
      <Fieldset legend="Zombie">
        <IdentityFields value={z} onChange={onChange} errors={errors} />
      </Fieldset>
      <Fieldset legend="Stats">
        {num('maxHealth', 'Max health', { integer: true })}
        <NumberField
          label="Skill challenge"
          hint="ADR 0066: 1..10, how much a kill teaches (default 5)."
          integer
          min={1}
          max={10}
          value={z.challenge ?? 5}
          issues={errors.challenge}
          onChange={(v) => onChange({ ...z, challenge: v ?? 0 })}
        />
        {num('moveSpeedMps', 'Move speed (m/s)')}
        {num('attackDamage', 'Attack damage', { integer: true })}
        {num('attackCooldownS', 'Attack cooldown (s)')}
        {num('hearingMultiplier', 'Hearing multiplier', {
          hint: 'Multiplies the noise radii it can hear (Runner > 1).',
        })}
        {num('sightRangeM', 'Sight range (m)', {
          hint: 'How far its vision cone reaches (shambler 7, runner 9, brute 6).',
        })}
        {num('knockbackForce', 'Knockback force', {
          hint: 'Impulse on the player per hit. 0 = none.',
        })}
        <NumberField
          label="Knockback resist"
          hint="0..1, the share of a weapon's knockback it shrugs off (brute 0.8)."
          min={0}
          max={1}
          step={0.05}
          value={z.knockbackResist ?? 0}
          issues={errors.knockbackResist}
          onChange={(v) => onChange({ ...z, knockbackResist: v ?? 0 })}
        />
        <NumberField
          label="Barricade damage (per s)"
          hint="0 = the raid default: a shambler bashes a door in 1.5 s."
          min={0}
          value={z.barricadeDps ?? 0}
          issues={errors.barricadeDps}
          onChange={(v) => onChange({ ...z, barricadeDps: v ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Wind attack (WIND)">
        <SelectField
          label="Attack"
          hint="grab: a push-off and a bite roll (attack damage = the bite's nominal cost). hit: a smash, wind through armor. mixed: hit at the hit chance, else grab. Required; not set reads grab."
          value={z.attack ?? null}
          none="(not set: grab)"
          options={ZOMBIE_ATTACKS.map((a) => ({ value: a, label: a }))}
          issues={errors.attack}
          onChange={(v) => onChange({ ...z, attack: v })}
        />
        {z.attack === 'mixed' && (
          <NumberField
            label="Hit chance"
            hint="0..1. The chance an attack is a hit (mixed only)."
            min={0}
            max={1}
            step={0.05}
            value={z.hitChance ?? ZOMBIE_WIND_DEFAULTS.hitChance}
            issues={errors.hitChance}
            onChange={(v) => onChange({ ...z, hitChance: v ?? 0 })}
          />
        )}
        <NumberField
          label="Push-off multiplier"
          hint="0.1..5. The push-off's wind cost against it."
          min={0.1}
          max={5}
          step={0.05}
          value={z.pushOffMult ?? ZOMBIE_WIND_DEFAULTS.pushOffMult}
          issues={errors.pushOffMult}
          onChange={(v) => onChange({ ...z, pushOffMult: v ?? 1 })}
        />
        <NumberField
          label="Bite odds multiplier"
          hint="0.1..5. Its grabs' bite roll."
          min={0.1}
          max={5}
          step={0.05}
          value={z.biteOddsMult ?? ZOMBIE_WIND_DEFAULTS.biteOddsMult}
          issues={errors.biteOddsMult}
          onChange={(v) => onChange({ ...z, biteOddsMult: v ?? 1 })}
        />
        <NumberField
          label="Grab telegraph (s)"
          hint="0.4..0.6. The warning before a grab lands."
          min={0.4}
          max={0.6}
          step={0.05}
          value={z.grabTelegraphS ?? ZOMBIE_WIND_DEFAULTS.grabTelegraphS}
          issues={errors.grabTelegraphS}
          onChange={(v) => onChange({ ...z, grabTelegraphS: v ?? 0.5 })}
        />
      </Fieldset>
      <Fieldset legend="Vision and stealth">
        {stealthNum('sightHalfAngleDeg', 'Sight half-angle (deg)', {
          hint: 'Half the cone angle, 0..180 (shambler 25 = a 50 degree cone). Walls and closed doors clip it.',
          max: 180,
        })}
        {stealthNum('noticeNearS', 'Notice near (s)', {
          hint: 'Seconds in the cone before it notices you up close. Above 0.',
        })}
        {stealthNum('noticeFarS', 'Notice far (s)', {
          hint: 'Seconds in the cone before it notices you at the edge of its range. At least notice near.',
        })}
        {stealthNum('sweepDeg', 'Idle sweep (deg)', {
          hint: 'Idle look sweep, degrees either side of its facing (0..180).',
          max: 180,
        })}
        {stealthNum('sweepPeriodMinS', 'Sweep period min (s)', {
          hint: 'Each zombie picks its sweep period in [min, max] from the raid seed.',
        })}
        {stealthNum('sweepPeriodMaxS', 'Sweep period max (s)')}
        {stealthNum('stealthKillS', 'Stealth kill time (s)', {
          hint: "A stealth kill on it takes at least this long. 0 = the weapon's time (brute 0.9).",
        })}
        {stealthNum('stealthKillNoiseM', 'Stealth kill noise (m)', {
          hint: 'A stealth kill on it makes at least this much noise. 0 = the raid rule (brute 4).',
        })}
        <CheckboxField
          label="Stealth immune"
          hint="Bosses: never stealth-killed."
          checked={z.stealthImmune ?? ZS.stealthImmune}
          onChange={(on) => onChange({ ...z, stealthImmune: on })}
        />
      </Fieldset>
      <Fieldset legend="Headshots">
        <NumberField
          label="Headshot multiplier"
          hint="Multiplies every headshot chance against it (brute 0.4). Above 0."
          min={0}
          step={0.05}
          value={z.headshotMult ?? 1}
          issues={errors.headshotMult}
          onChange={(v) => onChange({ ...z, headshotMult: v ?? 0 })}
        />
        <CheckboxField
          label="Head armor"
          hint="A helmet: a headshot is BLOCKED and does normal damage."
          checked={!!z.headArmor}
          onChange={(on) => onChange({ ...z, headArmor: on })}
        />
      </Fieldset>
    </>
  );
}

export function BaseModuleForm({ value: m, onChange, errors }: FormProps<Module>) {
  const levels = m.levels ?? [];
  return (
    <>
      <Fieldset legend="Module">
        <IdentityFields value={m} onChange={onChange} errors={errors} description />
      </Fieldset>
      <Fieldset legend="Levels">
        <p className="text-xs text-bruise sm:col-span-2">
          Skill requirements on levels (requiresSkill) are reserved for later (ADR 0066): nothing
          gates on them yet; edit in JSON.
        </p>
        <ListEditor
          label="Levels"
          hint="Level 1 first. Building level N costs row N."
          what="level"
          items={levels}
          issues={errors.levels}
          onChange={(next) => onChange({ ...m, levels: next })}
          make={() => ({
            level: levels.length + 1,
            cost: [],
            buildSeconds: 60,
            storageBonusKg: 0,
            waterPerDay: 0,
            healPerDay: 0,
            recruitChanceBonus: 0,
            repairBonus: 0,
          })}
          row={(l, j, set) => (
            <div className="grid gap-2 sm:grid-cols-2">
              <NumberField
                label="Level"
                integer
                min={1}
                value={l.level}
                issues={errors[`levels.${j}.level`]}
                onChange={(v) => set({ ...l, level: v ?? 1 })}
              />
              <NumberField
                label="Build time (s)"
                integer
                min={0}
                value={l.buildSeconds}
                issues={errors[`levels.${j}.buildSeconds`]}
                onChange={(v) => set({ ...l, buildSeconds: v ?? 0 })}
              />
              <NumberField
                label="Storage bonus (kg)"
                min={0}
                value={l.storageBonusKg}
                issues={errors[`levels.${j}.storageBonusKg`]}
                onChange={(v) => set({ ...l, storageBonusKg: v ?? 0 })}
              />
              <NumberField
                label="Water per day"
                integer
                min={0}
                value={l.waterPerDay}
                issues={errors[`levels.${j}.waterPerDay`]}
                onChange={(v) => set({ ...l, waterPerDay: v ?? 0 })}
              />
              <NumberField
                label="Rest multiplier (WIND, Infirmary)"
                hint={`0.1..1: rest hours at home x this (seed ${INFIRMARY_REST_MULTS.join(' / ')} by level). 0 = not set (reads 1).`}
                min={0}
                max={1}
                step={0.05}
                value={l.restMult ?? 0}
                issues={errors[`levels.${j}.restMult`]}
                onChange={(v) => set({ ...l, restMult: v ?? 0 })}
              />
              <NumberField
                label="Heal per day (old)"
                hint="Old, kept one content version: the rest multiplier replaces it."
                integer
                min={0}
                value={l.healPerDay}
                issues={errors[`levels.${j}.healPerDay`]}
                onChange={(v) => set({ ...l, healPerDay: v ?? 0 })}
              />
              <NumberField
                label="Recruit chance bonus (0..1, Radio)"
                min={0}
                max={1}
                value={l.recruitChanceBonus}
                issues={errors[`levels.${j}.recruitChanceBonus`]}
                onChange={(v) => set({ ...l, recruitChanceBonus: v ?? 0 })}
              />
              <NumberField
                label="Repair bonus (0.25 = +25%, Armory)"
                min={0}
                max={3}
                value={l.repairBonus}
                issues={errors[`levels.${j}.repairBonus`]}
                onChange={(v) => set({ ...l, repairBonus: v ?? 0 })}
              />
              <NumberField
                label="Infirmary beds"
                hint="Beds at this level (0 = none). Hurt survivors are admitted automatically."
                integer
                min={0}
                max={MAX_BEDS_PER_LEVEL}
                value={l.beds ?? 0}
                issues={errors[`levels.${j}.beds`]}
                onChange={(v) => set({ ...l, beds: v ?? 0 })}
              />
              {(l.beds ?? 0) > 0 && (
                <>
                  <NumberField
                    label="Bed duration multiplier"
                    hint="A patient's self-recovering conditions run this share of their home hours (above 0, at most 1)."
                    min={0}
                    max={1}
                    step={0.05}
                    value={l.bedDurationMult ?? 1}
                    issues={errors[`levels.${j}.bedDurationMult`]}
                    onChange={(v) => set({ ...l, bedDurationMult: v ?? 0 })}
                  />
                  <NumberField
                    label="Bed heal per day (old)"
                    hint="Old, kept one content version (WIND: the rest multiplier replaces it). HP a day on top of the recruit daily heal."
                    integer
                    min={0}
                    value={l.bedHealPerDay ?? 0}
                    issues={errors[`levels.${j}.bedHealPerDay`]}
                    onChange={(v) => set({ ...l, bedHealPerDay: v ?? 0 })}
                  />
                </>
              )}
              <NumberField
                label="Tier"
                integer
                min={0}
                value={l.tier ?? 0}
                issues={errors[`levels.${j}.tier`]}
                onChange={(v) => set({ ...l, tier: v ?? 0 })}
              />
              <NumberField
                label="Unlocks tier"
                integer
                min={0}
                value={l.unlocksTier ?? 0}
                issues={errors[`levels.${j}.unlocksTier`]}
                onChange={(v) => set({ ...l, unlocksTier: v ?? 0 })}
              />
              <NumberField
                label="Power use (Wh per day)"
                integer
                min={0}
                value={l.powerWhPerDay ?? 0}
                issues={errors[`levels.${j}.powerWhPerDay`]}
                onChange={(v) => set({ ...l, powerWhPerDay: v ?? 0 })}
              />
              <SelectField
                label="Room slot"
                none="(none)"
                value={l.roomSlot}
                options={ROOM_SLOTS.map((r) => ({ value: r, label: r }))}
                issues={errors[`levels.${j}.roomSlot`]}
                onChange={(v) => set({ ...l, roomSlot: v ?? '' })}
              />
              <div className="sm:col-span-2">
                <CheckboxField
                  label="Produces or stores power"
                  checked={!!l.power}
                  onChange={(on) =>
                    set({
                      ...l,
                      power: on ? { outputW: 0, storageWh: 0, solarWhPerHour: 0 } : null,
                    })
                  }
                />
              </div>
              {l.power && (
                <>
                  <NumberField
                    label="Output (W)"
                    integer
                    min={0}
                    value={l.power.outputW ?? 0}
                    issues={errors[`levels.${j}.power.outputW`]}
                    onChange={(v) => set({ ...l, power: { ...l.power, outputW: v ?? 0 } })}
                  />
                  <NumberField
                    label="Storage (Wh)"
                    integer
                    min={0}
                    value={l.power.storageWh ?? 0}
                    issues={errors[`levels.${j}.power.storageWh`]}
                    onChange={(v) => set({ ...l, power: { ...l.power, storageWh: v ?? 0 } })}
                  />
                  <NumberField
                    label="Solar (Wh per hour)"
                    integer
                    min={0}
                    value={l.power.solarWhPerHour ?? 0}
                    issues={errors[`levels.${j}.power.solarWhPerHour`]}
                    onChange={(v) => set({ ...l, power: { ...l.power, solarWhPerHour: v ?? 0 } })}
                  />
                </>
              )}
              <div className="sm:col-span-2">
                <RequirementsEditor
                  label={`Level ${l.level ?? j + 1} requires`}
                  base={`levels.${j}.requires`}
                  value={l.requires}
                  errors={errors}
                  onChange={(v) => set({ ...l, requires: v })}
                />
              </div>
              <StackListEditor
                label={`Level ${l.level ?? j + 1} cost`}
                base={`levels.${j}.cost`}
                value={l.cost}
                errors={errors}
                onChange={(v) => set({ ...l, cost: v })}
              />
            </div>
          )}
        />
      </Fieldset>
    </>
  );
}

export function CategoryForm({ value: c, onChange, errors }: FormProps<Category>) {
  const { lootTables } = useContentRefs();
  return (
    <>
      <Fieldset legend="Building category">
        <IdentityFields value={c} onChange={onChange} errors={errors} />
        <SelectField
          label="Loot table"
          value={c.lootTableId}
          options={lootTables.map((t) => ({ value: t.id ?? '', label: t.id ?? '' }))}
          issues={errors.lootTableId}
          onChange={(v) => onChange({ ...c, lootTableId: v ?? '' })}
        />
        <NumberField
          label="Difficulty"
          hint="1 (easy) to 5."
          integer
          min={1}
          max={5}
          value={c.difficulty}
          issues={errors.difficulty}
          onChange={(v) => onChange({ ...c, difficulty: v ?? 1 })}
        />
        <NumberField
          label="Loot rolls per room"
          min={0}
          step={0.1}
          value={c.lootRollsPerRoom}
          issues={errors.lootRollsPerRoom}
          onChange={(v) => onChange({ ...c, lootRollsPerRoom: v ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Mapping and card">
        <StringListEditor
          label="OSM tags"
          hint='"key=value" or "key=*". The first category with a matching tag wins, in list order.'
          placeholder="amenity=pharmacy"
          base="osmTags"
          value={c.osmTags}
          errors={errors}
          onChange={(v) => onChange({ ...c, osmTags: v })}
        />
        <ItemIdListEditor
          label="Likely loot"
          hint="Shown on the building card."
          base="likelyLoot"
          value={c.likelyLoot}
          errors={errors}
          onChange={(v) => onChange({ ...c, likelyLoot: v })}
        />
      </Fieldset>
    </>
  );
}
